"use client";

import React from "react";
import { ClerkLoaded, ClerkLoading, SignedIn, SignedOut } from "@clerk/nextjs";
import { Plus } from "lucide-react";
import { cn } from "utils/utils";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { SignUpButtonCTA } from "modules/auth/components/SignUpButtonCTA";
import { GameCard } from "modules/cards/model/card";
import { useAddCardModal } from "../stores/useAddCardModalStore";
import { TradeCard } from "modules/swap-calculator/model/tradeCard";

interface CardActionsButtonProps {
  card: GameCard | TradeCard;
  variant?: "button" | "icon" | "compact";
  className?: string;
  children?: React.ReactNode;
}

export function CardActionsButton({
  card,
  variant = "button",
  className,
  children,
}: CardActionsButtonProps) {
  const { openModal } = useAddCardModal();

  const handleClick = () => {
    openModal(card);
  };

  // Get default text based on variant
  const getDefaultText = () => {
    if (variant === "compact") return "Add";
    if (variant === "button") return "Add to...";
    return "";
  };

  // Use children if provided, otherwise use default text
  const buttonText = children || getDefaultText();

  // Signed out state - show sign up button
  const renderSignedOut = () => {
    switch (variant) {
      case "icon":
        return (
          <SignUpButtonCTA
            size="sm"
            variant="primary"
            className={cn("btn-circle", className)}
            icon={<Plus size={ICON_SIZE} />}
          >
            {""}
          </SignUpButtonCTA>
        );
      case "compact":
      case "button":
        return (
          <SignUpButtonCTA
            size="sm"
            variant="primary"
            className={className}
            icon={<Plus size={ICON_SIZE} />}
          >
            {buttonText}
          </SignUpButtonCTA>
        );
      default:
        return (
          <SignUpButtonCTA
            size="sm"
            variant="primary"
            className={className}
            icon={<Plus size={ICON_SIZE} />}
          >
            {buttonText}
          </SignUpButtonCTA>
        );
    }
  };

  // Signed in state - show add button
  const renderSignedIn = () => {
    switch (variant) {
      case "icon":
        return (
          <button
            type="button"
            onClick={handleClick}
            className={cn("btn btn-primary btn-circle btn-sm", className)}
            aria-label="Add card"
          >
            <Plus size={ICON_SIZE} />
          </button>
        );
      case "compact":
      case "button":
        return (
          <button
            type="button"
            onClick={handleClick}
            className={cn("btn btn-primary btn-sm", className)}
          >
            <Plus size={ICON_SIZE} />
            {buttonText}
          </button>
        );
      default:
        return (
          <button
            type="button"
            onClick={handleClick}
            className={cn("btn btn-primary btn-sm", className)}
          >
            <Plus size={ICON_SIZE} />
            {buttonText}
          </button>
        );
    }
  };

  const renderLoading = () => {
    switch (variant) {
      case "icon":
        return (
          <button
            type="button"
            className={cn("btn btn-primary btn-circle btn-sm", className)}
            aria-label="Add card"
          >
            <Plus size={ICON_SIZE} />
          </button>
        );
      case "compact":
      case "button":
        return (
          <button
            type="button"
            className={cn("btn btn-primary btn-sm", className)}
          >
            <Plus size={ICON_SIZE} />
            {buttonText}
          </button>
        );
      default:
        return (
          <button
            type="button"
            className={cn("btn btn-primary btn-sm", className)}
          >
            <Plus size={ICON_SIZE} />
            {buttonText}
          </button>
        );
    }
  };

  return (
    <>
      <ClerkLoading>{renderLoading()}</ClerkLoading>
      <ClerkLoaded>
        <SignedIn>{renderSignedIn()}</SignedIn>
        <SignedOut>{renderSignedOut()}</SignedOut>
      </ClerkLoaded>
    </>
  );
}
